import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ReadingProgress as Progress } from "../api";
import { Card, Spinner } from "./ui";
import { useAuth } from "../auth";

interface Props {
  bookId: number;
  totalChapters: number | null;
}

export function ReadingProgress({ bookId, totalChapters }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();

  const list = useQuery({
    queryKey: ["progress", bookId],
    queryFn: () => api.get<{ progress: Progress[] }>(`/progress/${bookId}`),
  });

  const set = useMutation({
    mutationFn: (currentChapter: number) =>
      api.post(`/progress/${bookId}`, { currentChapter }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["progress", bookId] }),
  });

  if (list.isLoading) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }

  const progress = list.data?.progress ?? [];
  const me = progress.find((p) => p.userId === user?.id);
  const myChapter = me?.currentChapter ?? 0;
  const max = totalChapters ?? Math.max(myChapter + 5, 20);

  return (
    <Card>
      <div className="flex items-center gap-3 mb-5">
        <span className="text-[0.7rem] uppercase tracking-[0.18em] text-gold">Reading along</span>
        <span className="h-px flex-1 bg-cream-200" />
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="md:w-72 shrink-0">
          <label className="text-xs text-plum-soft mb-2 block">I'm on chapter</label>
          <div className="flex items-center gap-3">
            <button
              onClick={() => set.mutate(Math.max(0, myChapter - 1))}
              className="w-9 h-9 rounded-full border border-cream-300 text-plum-soft hover:bg-cream-100 hover:border-rose-light transition-colors"
              aria-label="Decrement chapter"
            >
              −
            </button>
            <input
              type="number"
              min={0}
              max={max}
              value={myChapter}
              onChange={(e) => {
                const v = Math.max(0, Math.min(max, Number(e.target.value || 0)));
                set.mutate(v);
              }}
              className="flex-1 text-center font-serif text-2xl text-plum bg-cream-50 border border-cream-300 rounded-xl px-3 py-2 focus:outline-none focus:border-rose"
            />
            <button
              onClick={() => set.mutate(Math.min(max, myChapter + 1))}
              className="w-9 h-9 rounded-full border border-cream-300 text-plum-soft hover:bg-cream-100 hover:border-rose-light transition-colors"
              aria-label="Increment chapter"
            >
              +
            </button>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs text-plum-soft mb-3">The club's pace</div>
          <ul className="flex flex-col gap-2.5">
            {progress.map((p) => {
              const pct = max > 0 ? Math.min(100, (p.currentChapter / max) * 100) : 0;
              const initial = (p.name ?? p.email).trim().charAt(0).toUpperCase();
              const isMe = p.userId === user?.id;
              return (
                <li key={p.userId} className="flex items-center gap-3">
                  <div
                    className={[
                      "w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-serif shrink-0",
                      isMe
                        ? "bg-gradient-to-br from-rose to-rose-dark"
                        : "bg-gradient-to-br from-blush to-rose-light",
                    ].join(" ")}
                  >
                    {initial}
                  </div>
                  <div className="text-xs text-plum-soft w-20 truncate">
                    {isMe ? "You" : p.name ?? p.email.split("@")[0]}
                  </div>
                  <div className="flex-1 h-1.5 rounded-full bg-cream-200 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blush to-rose rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-xs text-plum-mute font-mono w-8 text-right">
                    {p.currentChapter}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Card>
  );
}
